import React from 'react';
import PropTypes from 'prop-types';

import {Box, CircularProgress, Stack, TextField, Typography} from 'views/components';
import {SearchOutlined as SearchOutlinedIcon} from 'views/icons';
import {translate} from 'views/module';

import backend from 'utils/backend';

const AdminTab = (props) => {
  const {
    onSelectAssignee,
  } = props;

  const [admins, setAdmins] = React.useState([]);
  const [assigneeSearch, setAssigneeSearch] = React.useState('');
  const [highlightedOptionIndex, setHighlightedOptionIndex] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);

  const rootRef = React.useRef(null);

  const assigneeSearchQuery = assigneeSearch.trim().toLowerCase();

  const getAdminFullName = (admin) => {
    return `${admin.firstName} ${admin.lastName || ''}`.trim();
  };

  const filteredAdmins = React.useMemo(() => {
    return admins.filter((admin) => {
      if (!assigneeSearchQuery) {
        return true;
      }

      return getAdminFullName(admin).toLowerCase().includes(assigneeSearchQuery);
    });
  }, [admins, assigneeSearchQuery]);

  React.useEffect(() => {
    setHighlightedOptionIndex(0);
  }, [assigneeSearchQuery]);

  React.useEffect(() => {
    rootRef.current?.focus({preventScroll: true});
  }, [rootRef]);

  React.useEffect(() => {
    let mounted = true;
    if (admins.length > 0) {
      return () => {
        mounted = false;
      };
    }

    const loadAdmins = async () => {
      setIsLoading(true);

      try {
        const filter = [
          {key: 'status', value: ['ACTIVE']},
          {key: 'role', value: 'ADMIN'},
        ];
        const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};
        const result = await backend.user.queryUser(filter, sortBy);

        if (mounted) {
          setAdmins(result.list || []);
        }
      } catch (error) {
        console.error('Failed to load admin users in assignee picker', error);
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadAdmins();

    return () => {
      mounted = false;
    };
  }, [admins.length]);

  const handleSearchKeyDown = React.useCallback((event) => {
    const isHandledKey = [
      'ArrowDown',
      'ArrowUp',
      'Enter',
    ].includes(event.key);

    if (!isHandledKey) {
      return;
    }

    event.preventDefault();

    if (!filteredAdmins.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      setHighlightedOptionIndex((previousIndex) => (previousIndex + 1) % filteredAdmins.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      setHighlightedOptionIndex((previousIndex) => (previousIndex - 1 + filteredAdmins.length) % filteredAdmins.length);
      return;
    }

    if (event.key === 'Enter') {
      const highlightedAssignee = filteredAdmins[highlightedOptionIndex] || filteredAdmins[0];

      if (highlightedAssignee) {
        onSelectAssignee('admins', highlightedAssignee);
      }
    }
  }, [filteredAdmins, highlightedOptionIndex, onSelectAssignee]);

  return (
    <Stack
      ref={rootRef}
      spacing={1.5}
      tabIndex={0}
      onKeyDown={handleSearchKeyDown}
    >
      <TextField
        fullWidth
        variant='outlined'
        value={assigneeSearch}
        onChange={setAssigneeSearch}
        placeholder={translate('tasks.Common.AssigneePicker.SearchPlaceholder')}
        icon={SearchOutlinedIcon}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        }}
      />
      <Box
        sx={{
          overflowY: 'auto',
          maxHeight: 290,
          pr: 0.5,
        }}
      >
        <Stack spacing={0.25}>
          {
            isLoading &&
              <Box sx={{display: 'flex', justifyContent: 'center', py: 1}}>
                <CircularProgress size={16} />
              </Box>
          }
          {
            !isLoading && filteredAdmins.map((admin, optionIndex) => (
              <Box
                key={`admins-${admin.userRn}`}
                component='button'
                onMouseEnter={() => setHighlightedOptionIndex(optionIndex)}
                onClick={() => onSelectAssignee('admins', admin)}
                sx={{
                  background: 'none',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: optionIndex === highlightedOptionIndex ? '#F7F4EF' : 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  px: 0.75,
                  py: 0.75,
                  color: '#23201C',
                  fontSize: '16px',
                }}
              >
                {getAdminFullName(admin)}
              </Box>
            ))
          }
          {
            !isLoading && filteredAdmins.length === 0 &&
              <Typography variant='body2' color='text.secondary' sx={{px: 0.75}}>
                {translate('tasks.Common.AssigneePicker.NoAdminUsersFound')}
              </Typography>
          }
        </Stack>
      </Box>
    </Stack>
  );
};

AdminTab.propTypes = {
  onSelectAssignee: PropTypes.func.isRequired,
};

export default React.memo(AdminTab);
